(() => {
  if (customElements.get('pw-oil-catalog')) return;
  const normalize = value => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase().trim();
  const decoder = document.createElement('textarea');
  const decode = value => { decoder.innerHTML = value; return decoder.value.replace(/\s+/g, ' ').trim(); };
  const capitalize = value => value.charAt(0).toLocaleUpperCase() + value.slice(1);
  // Small synonym map: normalized note -> the one note we show (add obvious duplicates here).
  const SYNONYMS = {
    'musk': 'muskus', 'witte muskus': 'muskus', 'wit muskus': 'muskus', 'white musk': 'muskus',
    'ceder': 'cederhout', 'cedar': 'cederhout', 'cedarwood': 'cederhout',
    'irisbloem': 'iris', 'iriswortel': 'iris', 'iris root': 'iris', 'orris': 'iris',
    'wit sandelhout': 'sandelhout', 'witte sandelhout': 'sandelhout', 'sandalwood': 'sandelhout',
    'viooltje': 'viooltjes', 'violet': 'viooltjes', 'violets': 'viooltjes'
  };
  const canonical = key => SYNONYMS[key] || key;
  // "Amber en musk" -> two notes
  const splitNotes = raw => decode(raw).split(/\s+(?:en|and)\s+/i).map(part => part.trim()).filter(Boolean);
  const phone = window.matchMedia('(max-width: 699px)');

  class OilCatalog extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.tools = this.querySelector('[data-oil-tools]');
      this.search = this.querySelector('[data-oil-search]');
      this.searchClear = this.querySelector('[data-oil-search-clear]');
      this.dialog = this.querySelector('[data-oil-filters]');
      this.openButton = this.querySelector('[data-oil-filter-open]');
      this.chips = this.querySelector('[data-oil-chips]');
      this.clearAll = this.querySelector('.pw-oils__clear-all');
      const labels = new Map();
      const keys = new Set();
      this.records = [...this.querySelectorAll('[data-oil-card]')].map(card => {
        const content = card.querySelector('[data-oil-metadata]').content;
        const raw = [];
        const cardNotes = new Set();
        content.querySelectorAll('[data-oil-note]').forEach(el => {
          splitNotes(el.textContent).forEach(part => {
            const own = normalize(part);
            const key = canonical(own);
            if (!key) return;
            raw.push(own);
            cardNotes.add(key);
            keys.add(key);
            if (own === key && !labels.has(key)) labels.set(key, capitalize(part.toLocaleLowerCase()));
          });
        });
        // Search on title + notes only (not the long description)
        const text = normalize((card.dataset.title || '') + ' ' + raw.join(' ') + ' ' + [...cardNotes].join(' '));
        return { card, notes: [...cardNotes], text };
      });
      keys.forEach(key => { if (!labels.has(key)) labels.set(key, capitalize(key)); });
      this.labels = labels;
      const lang = document.documentElement.lang || undefined;
      const noteOptions = this.querySelector('[data-oil-note-options]');
      this.noteOptions = noteOptions;
      noteOptions.replaceChildren();
      [...keys].map(key => [key, labels.get(key)]).sort((a, b) => a[1].localeCompare(b[1], lang)).forEach(([key, name]) => {
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.value = key;
        const span = document.createElement('span');
        span.textContent = name;
        label.append(input, span);
        noteOptions.append(label);
      });
      this.tools.hidden = false;
      this.openButton.hidden = !keys.size;
      this.search.addEventListener('input', () => this.filter(), options);
      this.search.addEventListener('focus', () => this.revealOnFocus(), options);
      this.searchClear.addEventListener('click', () => {
        this.search.value = '';
        this.filter();
        this.search.focus({ preventScroll: true });
      }, options);
      noteOptions.addEventListener('change', () => this.filter(), options);
      this.chips.addEventListener('click', event => {
        const chip = event.target.closest('[data-oil-remove]');
        if (!chip) return;
        const input = [...noteOptions.querySelectorAll('input')].find(el => el.value === chip.dataset.oilRemove);
        if (input) input.checked = false;
        this.filter();
      }, options);
      this.openButton.addEventListener('click', () => {
        this.dialog.showModal();
        document.documentElement.classList.add('pw-oils-filters-open');
      }, options);
      this.querySelectorAll('[data-oil-close]').forEach(button => button.addEventListener('click', () => this.dialog.close(), options));
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('pw-oils-filters-open');
        this.openButton.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.querySelectorAll('[data-oil-clear]').forEach(button => button.addEventListener('click', () => {
        this.search.value = '';
        noteOptions.querySelectorAll('input').forEach(input => { input.checked = false; });
        this.filter();
      }, options));
      this.filter();
    }
    disconnectedCallback() {
      this.abortController.abort();
      clearTimeout(this.revealTimer);
      if (this.dialog.open) this.dialog.close();
      document.documentElement.classList.remove('pw-oils-filters-open');
    }
    // Phone keyboard covers the results: put the search bar just under the sticky header while typing.
    revealOnFocus() {
      if (!phone.matches) return;
      clearTimeout(this.revealTimer);
      const scrollToBar = behavior => {
        // Probe what is really stuck to the top right now (the header wrapper can be taller than what is visible).
        let offset = 0;
        const x = Math.round(window.innerWidth / 2);
        const stuck = el => { for (; el && el !== document.documentElement; el = el.parentElement) { const position = getComputedStyle(el).position; if (position === 'fixed' || position === 'sticky') return true; } return false; };
        for (let y = 0; y < 240; y += 4) {
          const el = document.elementFromPoint(x, y);
          if (!el || this.contains(el) || !stuck(el)) break;
          offset = y + 4;
        }
        const top = this.tools.getBoundingClientRect().top - offset - 8;
        if (Math.abs(top) > 4) window.scrollBy({ top, behavior });
      };
      // wait for the keyboard animation, then correct once more after the header settled
      this.revealTimer = setTimeout(() => {
        scrollToBar('smooth');
        this.revealTimer = setTimeout(() => scrollToBar('auto'), 450);
      }, 250);
    }
    filter() {
      const query = normalize(this.search.value);
      const selected = [...this.noteOptions.querySelectorAll('input:checked')];
      const keys = selected.map(el => el.value);
      let count = 0;
      this.records.forEach(({ card, notes, text }) => {
        const matches = text.includes(query) && (!keys.length || keys.some(note => notes.includes(note)));
        card.hidden = !matches;
        if (matches) count++;
      });
      this.querySelector('[data-oil-count]').textContent = this.dataset.countLabel.replace('[count]', count);
      this.querySelector('[data-oil-filter-count]').textContent = keys.length ? `(${keys.length})` : '';
      this.querySelector('[data-oil-empty]').hidden = count !== 0;
      this.searchClear.hidden = !this.search.value;
      this.clearAll.hidden = !(keys.length || this.search.value);
      this.chips.replaceChildren(...selected.map(input => {
        const name = this.labels.get(input.value) || input.value;
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'pw-oils__chip';
        chip.dataset.oilRemove = input.value;
        chip.setAttribute('aria-label', (this.dataset.removeLabel || '').replace('[name]', name));
        const text = document.createElement('span');
        text.textContent = name;
        const cross = document.createElement('span');
        cross.className = 'pw-oils__chip-x';
        cross.setAttribute('aria-hidden', 'true');
        cross.textContent = '×';
        chip.append(text, cross);
        return chip;
      }));
      this.chips.hidden = !keys.length;
    }
  }
  customElements.define('pw-oil-catalog', OilCatalog);
})();
